// FormPreviewOverlay — the builder's Preview action: a full-bleed dimmed
// overlay with a centered phone frame rendering the form as the mobile app
// shows it, built from the LIVE builder state (unsaved edits included — that's
// the point of previewing before saving). Read-only v1: nothing is fillable,
// the answered counter stays at 0, and navigation is the footer's Back/Next
// only.
//
// Not a Modal: the shared primitive maxes out at size='lg' with a footer,
// while this is full-bleed with a floating close button. The portal / Escape /
// focus-restore idioms are mirrored from ReportPdfOverlay (itself mirroring
// Modal.tsx). The backdrop is inert — only the ✕ and Escape close it.
//
// The phone chrome (status bar, notch, home indicator) is decorative and
// aria-hidden; the screen content is real user data, so the dialog itself is
// announced (the WelcomeScreenPreview rule). Footer buttons are native
// <button>s styled as the mobile CTAs — the shared Button's desktop chrome
// would break the device fiction.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon, IconButton } from '@/components/common';
import type { BuilderField, BuilderPage } from './utils/builderModel';
import { deriveCanvasItems } from './utils/canvasItems';
import { basicRecord } from './utils/formElementDefaults';
import FormPreviewFieldCard from './FormPreviewFieldCard';

interface FormPreviewOverlayProps {
  open: boolean;
  onClose: () => void;
  /** Form title shown inside the phone header. */
  title: string;
  /** The LIVE builder pages — unsaved edits included. */
  pages: BuilderPage[];
  /** Page the canvas is on — the preview opens there. */
  activePageId: string;
}

const isHidden = (f: BuilderField) =>
  'validationProperties' in f.properties &&
  (f.properties.validationProperties as Record<string, unknown>).hidden === true;

/** One page's cards: sections render as a group heading over their children,
 *  hidden fields are skipped from render and numbering, and numbering restarts
 *  at 1 per page. Conditional follow-up (child) questions are not shown at all
 *  (user decision) — they ride in `item.followUps`, which this walk ignores. */
const PreviewPageBody = ({ page }: { page: BuilderPage }) => {
  const { t } = useTranslation();
  const items = deriveCanvasItems(page.fields);

  let counter = 0;
  const blocks = items.flatMap((item) => {
    if (item.kind === 'section') {
      if (isHidden(item.section)) return [];
      const children = item.children.filter((c) => !isHidden(c.field));
      const sectionTitle =
        ((basicRecord(item.section.properties).sectionTitle as string) ?? '').trim() ||
        t('forms_builder_untitled_section');
      return [
        <div key={item.section.id} className='form-preview__section'>
          <span className='form-preview__section-title'>{sectionTitle}</span>
          {children.map((c) => {
            counter += 1;
            return <FormPreviewFieldCard key={c.field.id} item={c} badge={counter} />;
          })}
        </div>,
      ];
    }
    if (isHidden(item.field)) return [];
    counter += 1;
    return [<FormPreviewFieldCard key={item.field.id} item={item} badge={counter} />];
  });

  if (counter === 0) {
    return <div className='form-preview__empty'>{t('forms_builder_preview_empty')}</div>;
  }
  return <>{blocks}</>;
};

const FormPreviewOverlay = ({
  open,
  onClose,
  title,
  pages,
  activePageId,
}: FormPreviewOverlayProps) => {
  const { t } = useTranslation();
  const [pageIndex, setPageIndex] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Escape closes; focus moves into the dialog on open and back on close. The
  // preview also opens on the page the canvas is on.
  useEffect(() => {
    if (!open) return;
    const idx = pages.findIndex((p) => p.id === activePageId);
    setPageIndex(idx >= 0 ? idx : 0);
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreFocusRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || pages.length === 0) return null;

  const current = Math.min(pageIndex, pages.length - 1);
  const page = pages[current];
  const isFirst = current === 0;
  const isLast = current === pages.length - 1;
  // The design's "0 / 18 answered" denominator: every question across the
  // whole form — sections aren't questions, and follow-up (child) rows aren't
  // shown in the preview, so neither counts.
  const fieldTotal = pages.reduce(
    (n, p) =>
      n + p.fields.filter((f) => f.elementType !== 'section' && f.triggerParentId === null).length,
    0,
  );

  return createPortal(
    <div className='form-preview'>
      <IconButton
        icon='x'
        variant='ghost'
        className='form-preview__close'
        aria-label={t('modal_close')}
        onClick={onClose}
      />

      <div
        ref={panelRef}
        className='form-preview__phone'
        role='dialog'
        aria-modal='true'
        aria-label={title}
        tabIndex={-1}
      >
        <div className='form-preview__screen'>
          <div className='form-preview__head'>
            <div className='form-preview__status' aria-hidden='true'>
              <span className='form-preview__status-time'>9:41</span>
              <span className='form-preview__status-notch' />
              <span className='form-preview__status-icons'>
                <Icon name='signal' size={13} />
                <Icon name='wifi' size={13} />
                <Icon name='battery-full' size={15} />
              </span>
            </div>
            <span className='form-preview__form-title'>{title}</span>
            <div className='form-preview__meta'>
              <span className='form-preview__meta-page'>
                <span className='form-preview__meta-ico'>
                  <Icon name='layers' size={14} />
                </span>
                {t('forms_builder_preview_page_of', { current: current + 1, total: pages.length })}
              </span>
              <span className='form-preview__meta-answered'>
                {t('forms_builder_preview_answered', { answered: 0, total: fieldTotal })}
              </span>
            </div>
            <div className='form-preview__progress' aria-hidden='true'>
              {pages.map((p, i) => (
                <span
                  key={p.id}
                  className={[
                    'form-preview__progress-seg',
                    i === current && 'form-preview__progress-seg--active',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                />
              ))}
            </div>
          </div>

          <div className='form-preview__body'>
            <PreviewPageBody key={page.id} page={page} />
          </div>

          <div className='form-preview__footer'>
            {!isFirst && (
              <button
                type='button'
                className='form-preview__nav-btn form-preview__nav-btn--secondary'
                onClick={() => setPageIndex(current - 1)}
              >
                <span className='form-preview__nav-arrow'>
                  <Icon name='chevron-left' size={16} />
                </span>
                {t('forms_builder_preview_back')}
              </button>
            )}
            {!isLast && (
              <button
                type='button'
                className='form-preview__nav-btn form-preview__nav-btn--primary'
                onClick={() => setPageIndex(current + 1)}
              >
                {t('forms_builder_preview_next')}
                <span className='form-preview__nav-arrow'>
                  <Icon name='arrow-right' size={16} />
                </span>
              </button>
            )}
          </div>
          <span className='form-preview__home' aria-hidden='true' />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FormPreviewOverlay;
